import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, User, ShieldCheck, CheckCircle2, Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { useToast } from "../context/ToastContext";
import * as profileApi from "../api/profileApi";
import "./AccountForms.css";

export default function Profile() {
  const { userId } = useAuth();
  const { t } = useLanguage();
  const { addToast } = useToast();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const p = await profileApi.getProfile(userId);
        if (!cancelled && p) {
          setFirstName(p.firstName || "");
          setLastName(p.lastName || "");
        }
      } catch (e) {
        if (!cancelled) addToast(t("profile.loadError"), "error");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, addToast, t]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await profileApi.updateProfile(userId, {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      });
      addToast(t("profile.success"), "success");
    } catch (err) {
      addToast(err?.message || t("profile.error"), "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="form-page">
        <div className="page-loading">
          <span className="page-loading__dot" />
        </div>
      </div>
    );
  }

  const initials = [firstName, lastName]
    .filter(Boolean)
    .map((s) => s.charAt(0).toUpperCase())
    .join("");

  return (
    <div className="form-page">
      <div className="container form-page__inner">
        <Link to="/account" className="form-back btn-ghost">
          <ArrowLeft size={15} className="rtl-flip" />
          <span>{t("profile.back")}</span>
        </Link>

        <p className="section-label">{t("profile.sectionLabel")}</p>
        <h1 className="form-page__title">{t("profile.title")}</h1>

        {/* Member Preview Card */}
        <div className="profile-identity-card">
          <div className="profile-identity-avatar">
            {initials ? initials : <User size={22} />}
          </div>
          <div className="profile-identity-info">
            <h3 className="profile-identity-name">
              {[firstName, lastName].filter(Boolean).join(" ") || t("account.memberBadge")}
            </h3>
            <div className="profile-identity-badge">
              <ShieldCheck size={13} />
              <span>{t("profile.verified")}</span>
            </div>
          </div>
        </div>

        {/* Profile Edit Form */}
        <form className="account-form" onSubmit={handleSubmit}>
          <div className="auth-field">
            <label htmlFor="fn">{t("profile.firstName")}</label>
            <input
              id="fn"
              value={firstName}
              placeholder={t("profile.firstNamePlaceholder")}
              onChange={(e) => setFirstName(e.target.value)}
              required
            />
          </div>

          <div className="auth-field">
            <label htmlFor="ln">{t("profile.lastName")}</label>
            <input
              id="ln"
              value={lastName}
              placeholder={t("profile.lastNamePlaceholder")}
              onChange={(e) => setLastName(e.target.value)}
              required
            />
          </div>

          <button type="submit" className="btn-primary" disabled={saving} style={{ width: "100%" }}>
            {saving ? (
              <>
                <Loader2 size={16} className="spin-animate" />
                <span>{t("profile.saving")}</span>
              </>
            ) : (
              t("profile.save")
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
